import { useCallback, useEffect, useRef } from 'react'

const PA_RESPONSE_TYPES = ['PAreadZH', 'PAreadKlaw', 'PAread', 'RobokiPAread']
const FIRST_RETRY_MS = 700
const BACKOFF_RETRY_MS = 2000
const STOP_BYTE = 'M8'

const useAckPaPolling = ({
    worker,
    buildBytes,
    shouldContinue,
    intervalMs = 200,
    responseTypes = PA_RESPONSE_TYPES,
}) => {
    const runningRef = useRef(false)
    const timerRef = useRef(null)
    const unackedRef = useRef(0)
    const latestRef = useRef({})
    latestRef.current = {
        worker,
        buildBytes,
        shouldContinue,
        intervalMs,
        responseTypes,
    }

    const clearTimer = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current)
            timerRef.current = null
        }
    }

    const stop = useCallback(() => {
        clearTimer()
        unackedRef.current = 0
        if (!runningRef.current) return
        runningRef.current = false
        latestRef.current.worker?.postMessage({
            type: 'write',
            value: STOP_BYTE,
        })
    }, [])

    const sendRequest = useCallback(() => {
        clearTimer()
        const { worker, buildBytes, shouldContinue } = latestRef.current
        if (!runningRef.current) return
        if (shouldContinue && !shouldContinue()) {
            stop()
            return
        }
        worker.postMessage({ type: 'writeArray', value: buildBytes() })
        unackedRef.current += 1
        timerRef.current = setTimeout(
            sendRequest,
            unackedRef.current >= 2 ? BACKOFF_RETRY_MS : FIRST_RETRY_MS
        )
    }, [stop])

    const start = useCallback(() => {
        if (runningRef.current) return
        runningRef.current = true
        unackedRef.current = 0
        sendRequest()
    }, [sendRequest])

    useEffect(() => {
        if (!worker) return undefined
        const handleMessage = (e) => {
            if (!runningRef.current) return
            if (!latestRef.current.responseTypes.includes(e.data?.type)) return
            unackedRef.current = 0
            clearTimer()
            timerRef.current = setTimeout(
                sendRequest,
                latestRef.current.intervalMs
            )
        }
        worker.addEventListener('message', handleMessage)
        return () => {
            worker.removeEventListener('message', handleMessage)
        }
    }, [worker, sendRequest])

    useEffect(() => stop, [stop])

    return { start, stop, isRunning: () => runningRef.current }
}

export default useAckPaPolling
